import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar as CalendarIcon, CheckCircle, ArrowRight, ArrowLeft, Loader2, Search, Plus, X } from 'lucide-react';
import { ServiceBasket } from '../components/ServiceBasket';
import { siteInfo } from '../data/mock';
import { toast } from 'sonner';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState('');

  // Services basket (multi-services)
  const [basket, setBasket] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: ''
  });

  // API data states
  const [services, setServices] = useState([]);
  const [discountConfig, setDiscountConfig] = useState(null);
  const [allTimeSlots, setAllTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Fetch services and discount config on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesRes = await axios.get(`${API}/services`);
        setServices(servicesRes.data.services.filter(s => s.isActive));

        // Try to fetch discount config (may not be available publicly)
        try {
          const discountRes = await axios.get(`${API}/admin/discount-config`);
          setDiscountConfig(discountRes.data);
        } catch (err) {
          console.log('Discount config not available');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Erreur lors du chargement des données');
      }
    };
    fetchData();
  }, []);

  // Parse duration string to minutes
  const parseDuration = useCallback((duration) => {
    let minutes = 0;
    if (duration.includes('h')) {
      const parts = duration.split('h');
      minutes += parseInt(parts[0]) * 60;
      if (parts[1] && parts[1].trim()) {
        minutes += parseInt(parts[1].replace(/\D/g, '')) || 0;
      }
    } else if (duration.includes('min')) {
      minutes = parseInt(duration.replace(/\D/g, '')) || 0;
    }
    return minutes || 30;
  }, []);

  // Format minutes to readable duration
  const formatDuration = useCallback((minutes) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours}h` : `${hours}h${mins.toString().padStart(2, '0')}`;
  }, []);

  // Parse price string to float
  const parsePrice = useCallback((priceStr) => {
    const numbers = priceStr.match(/\d+([.,]\d+)?/);
    return numbers ? parseFloat(numbers[0].replace(',', '.')) : 0;
  }, []);

  // Calculate basket totals
  const basketTotals = useMemo(() => {
    if (basket.length === 0) {
      return {
        totalDurationMinutes: 0,
        totalDuration: '0min',
        subtotal: 0,
        discountPercent: 0,
        discount: 0,
        totalPrice: 0,
        numServices: 0
      };
    }

    // Calculate total duration
    let totalMinutes = 0;
    basket.forEach(service => {
      const duration = service.duration.toLowerCase();
      if (duration.includes('-')) {
        const parts = duration.split('-')[0].trim();
        totalMinutes += parseDuration(parts);
      } else {
        totalMinutes += parseDuration(duration);
      }
    });

    // Calculate subtotal
    let subtotal = 0;
    basket.forEach(service => {
      subtotal += parsePrice(service.price);
    });

    // Calculate discount
    let discountPercent = 0;
    let discount = 0;

    if (discountConfig && discountConfig.isEnabled) {
      const sortedTiers = [...discountConfig.tiers].sort((a, b) => b.minServices - a.minServices);
      const applicableTier = sortedTiers.find(t => basket.length >= t.minServices);
      if (applicableTier) {
        discountPercent = applicableTier.discountPercent;
        discount = subtotal * (discountPercent / 100);
      }
    }

    const totalPrice = subtotal - discount;

    return {
      totalDurationMinutes: totalMinutes,
      totalDuration: formatDuration(totalMinutes),
      subtotal,
      discountPercent,
      discount,
      totalPrice,
      numServices: basket.length
    };
  }, [basket, discountConfig]);

  // Fetch available slots when date changes
  const fetchAvailableSlots = useCallback(async (date) => {
    if (!date) return;

    setLoading(true);
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      // Passer la durée du panier pour filtrer les créneaux disponibles
      const duration = basketTotals.totalDurationMinutes || 0;
      const response = await axios.get(`${API}/bookings/available-slots?date=${dateStr}&duration=${duration}`);

      setAllTimeSlots(response.data.allSlots);
      setBookedSlots(response.data.bookedSlots);

      // Afficher un message si le jour est complet
      if (response.data.isDayFull) {
        toast.error('Cette journée est complète. Veuillez choisir une autre date.');
      } else if (response.data.availableSlots.length === 0 && duration > 0) {
        toast.warning(`Aucun créneau disponible pour une durée de ${basketTotals.totalDuration}. Veuillez choisir une autre date ou réduire le nombre de prestations.`);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Erreur lors du chargement des créneaux');
    } finally {
      setLoading(false);
    }
  }, [basketTotals.totalDurationMinutes, basketTotals.totalDuration]);

  // Refresh slots when date or basket changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
      setSelectedTime('');
    }
  }, [selectedDate, fetchAvailableSlots]);

  // Also refresh when basket changes (duration impacts available slots)
  useEffect(() => {
    if (selectedDate && basket.length > 0) {
      fetchAvailableSlots(selectedDate);
    }
  }, [basket.length, selectedDate, fetchAvailableSlots]);

  // Get services for selected category with search filter
  const categoryServices = useMemo(() => {
    if (!selectedCategory) return [];
    const category = services.find(s => s.category === selectedCategory);
    if (!category) return [];

    const items = category.items || [];

    // Filter items based on search query
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      item.name.toLowerCase().includes(query)
    );
  }, [selectedCategory, services, searchQuery]);

  // Disable past dates, Tuesdays, Wednesdays
  const disabledDays = useMemo(() => {
    return (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dayOfWeek = date.getDay();
      return date < today || dayOfWeek === 2 || dayOfWeek === 3;
    };
  }, []);

  // Add service to basket
  const handleAddToBasket = () => {
    if (!selectedCategory || !selectedService) {
      toast.error('Veuillez sélectionner un service');
      return;
    }

    const serviceDetails = getServiceDetails(selectedCategory, selectedService);
    if (!serviceDetails) return;

    const newService = {
      category: selectedCategory,
      name: selectedService,
      price: serviceDetails.price,
      duration: serviceDetails.duration
    };

    setBasket(prev => [...prev, newService]);
    setSelectedCategory('');
    setSelectedService('');
    setSearchQuery('');
    toast.success('Service ajouté au panier !');
  };

  // Remove service from basket
  const handleRemoveFromBasket = (index) => {
    setBasket(prev => prev.filter((_, i) => i !== index));
    toast.info('Service retiré du panier');
  };

  // Get service details
  const getServiceDetails = (category, serviceName) => {
    const cat = services.find(s => s.category === category);
    if (!cat) return null;
    return cat.items.find(item => item.name === serviceName);
  };

  // Submit booking
  const handleSubmit = async () => {
    if (basket.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }

    if (basketTotals.totalDurationMinutes > 300) {
      toast.error('La durée totale ne peut pas dépasser 5 heures');
      return;
    }

    setSubmitting(true);
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const response = await axios.post(`${API}/bookings`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        services: basket,
        date: dateStr,
        time: selectedTime,
        notes: formData.notes || null
      });

      setBookingResult(response.data.booking);
      toast.success(response.data.message);
      setStep(4);
    } catch (error) {
      console.error('Error creating booking:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la réservation';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Validation
  const canProceedStep1 = basket.length > 0 && basketTotals.totalDurationMinutes <= 300;
  const canProceedStep2 = selectedDate && selectedTime;
  const canProceedStep3 = formData.firstName && formData.lastName && formData.email && formData.phone;

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero */}
      <section className="relative py-16 bg-[#2C1810]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Réservation</span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mt-4 mb-6">
            Prendre Rendez-vous
          </h1>
          <div className="w-20 h-1 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B] mx-auto mb-6" />
          <p className="text-[#D4C4B5] text-lg max-w-xl mx-auto">
            Réservez une ou plusieurs prestations en quelques clics
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s
                    ? 'bg-[#C9A96E] text-white'
                    : 'bg-[#E8DDD4] text-[#5D4037]'
                }`}
              >
                {step > s ? <CheckCircle size={20} /> : s}
              </div>
              {s < 4 && (
                <div
                  className={`w-12 md:w-20 h-1 mx-2 rounded ${
                    step > s ? 'bg-[#C9A96E]' : 'bg-[#E8DDD4]'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-center mt-4 text-sm text-[#5D4037]">
          <span className={`mx-2 md:mx-4 ${step === 1 ? 'text-[#C9A96E] font-semibold' : ''}`}>Services</span>
          <span className={`mx-2 md:mx-4 ${step === 2 ? 'text-[#C9A96E] font-semibold' : ''}`}>Date & Heure</span>
          <span className={`mx-2 md:mx-4 ${step === 3 ? 'text-[#C9A96E] font-semibold' : ''}`}>Informations</span>
          <span className={`mx-2 md:mx-4 ${step === 4 ? 'text-[#C9A96E] font-semibold' : ''}`}>Confirmation</span>
        </div>
      </div>

      {/* Booking Content */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Step 1: Select Services */}
          {step === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Service Selection */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg animate-fade-in">
                  <h2 className="text-2xl font-serif font-bold text-[#2C1810] mb-6">
                    Choisissez vos prestations
                  </h2>

                  {/* Category & Service Selection */}
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Catégorie de service *</Label>
                      <Select
                        value={selectedCategory}
                        onValueChange={(value) => {
                          setSelectedCategory(value);
                          setSelectedService('');
                          setSearchQuery(''); // Reset search when category changes
                        }}
                      >
                        <SelectTrigger className="w-full h-12 border-[#D4C4B5]">
                          <SelectValue placeholder="Sélectionnez une catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.category}>
                              {service.category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedCategory && (
                      <div className="space-y-4 animate-fade-in">
                        {/* Search Bar for Prestations */}
                        <div>
                          <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
                            <Input
                              type="text"
                              placeholder="Rechercher une prestation..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="pl-12 h-12 border-[#D4C4B5] focus:border-[#C9A96E]"
                            />
                            {searchQuery && (
                              <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A67B5B] hover:text-[#2C1810]"
                              >
                                <X size={18} />
                              </button>
                            )}
                          </div>
                          {searchQuery && categoryServices.length > 0 && (
                            <p className="text-sm text-[#5D4037] mt-2">
                              {categoryServices.length} prestation{categoryServices.length > 1 ? 's' : ''} trouvée{categoryServices.length > 1 ? 's' : ''}
                            </p>
                          )}
                          {searchQuery && categoryServices.length === 0 && (
                            <p className="text-sm text-red-600 mt-2">
                              Aucune prestation trouvée
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Prestation *</Label>
                          <Select value={selectedService} onValueChange={setSelectedService}>
                            <SelectTrigger className="w-full h-12 border-[#D4C4B5]">
                              <SelectValue placeholder="Sélectionnez une prestation" />
                            </SelectTrigger>
                            <SelectContent>
                              {categoryServices.map((item, index) => (
                                <SelectItem key={index} value={item.name}>
                                  {item.name} - {item.price} ({item.duration})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {selectedService && (
                      <Button
                        onClick={handleAddToBasket}
                        className="w-full bg-[#C9A96E] hover:bg-[#A67B5B] text-white h-12 font-semibold"
                      >
                        <Plus className="mr-2" size={20} />
                        Ajouter au panier
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Basket (Sidebar on desktop, below on mobile) */}
              <div className="lg:col-span-1">
                <div className="sticky top-6">
                  <ServiceBasket
                    services={basket}
                    onRemoveService={handleRemoveFromBasket}
                    totals={basketTotals}
                  />

                  {basket.length > 0 && (
                    <div className="mt-6">
                      <Button
                        onClick={() => setStep(2)}
                        disabled={!canProceedStep1}
                        className="w-full bg-[#2C1810] hover:bg-[#1a0f08] text-white h-12 font-semibold"
                      >
                        Continuer
                        <ArrowRight className="ml-2" size={20} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Select Date & Time */}
          {step === 2 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-in">
              <h2 className="text-2xl font-serif font-bold text-[#2C1810] mb-2">
                Choisissez la date et l'heure
              </h2>
              {basketTotals.totalDurationMinutes > 0 && (
                <p className="text-[#5D4037] mb-6">
                  Votre rendez-vous durera <span className="font-semibold text-[#C9A96E]">{basketTotals.totalDuration}</span>
                </p>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Calendar */}
                <div>
                  <Label className="mb-4 block">Date *</Label>
                  <div className="bg-[#F5F0EB] rounded-xl p-4 inline-block">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      locale={fr}
                      className="rounded-md"
                    />
                  </div>
                </div>

                {/* Time Slots */}
                <div>
                  <Label className="mb-4 block">Heure *</Label>
                  {selectedDate ? (
                    loading ? (
                      <div className="bg-[#F5F0EB] rounded-xl p-8 text-center">
                        <Loader2 className="mx-auto text-[#C9A96E] mb-2 animate-spin" size={40} />
                        <p className="text-[#5D4037]">Chargement des créneaux...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {allTimeSlots.map((slot) => {
                          const isBooked = bookedSlots.includes(slot);
                          return (
                            <button
                              key={slot}
                              onClick={() => !isBooked && setSelectedTime(slot)}
                              disabled={isBooked}
                              className={`time-slot ${selectedTime === slot ? 'selected' : ''} ${
                                isBooked ? 'opacity-40 cursor-not-allowed line-through' : ''
                              }`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <div className="bg-[#F5F0EB] rounded-xl p-8 text-center">
                      <CalendarIcon className="mx-auto text-[#C9A96E] mb-2" size={40} />
                      <p className="text-[#5D4037]">Sélectionnez d'abord une date</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Selection Summary */}
              {selectedDate && selectedTime && (
                <div className="mt-6 bg-[#F5F0EB] p-4 rounded-xl animate-fade-in">
                  <p className="text-[#2C1810] font-semibold">
                    {formatDate(selectedDate)} à {selectedTime}
                  </p>
                  <p className="text-sm text-[#5D4037] mt-1">
                    Votre rendez-vous se terminera vers{' '}
                    {(() => {
                      const [hours, minutes] = selectedTime.split(':').map(Number);
                      const endMinutes = hours * 60 + minutes + basketTotals.totalDurationMinutes;
                      const endHours = Math.floor(endMinutes / 60);
                      const endMins = endMinutes % 60;
                      return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
                    })()}
                  </p>
                </div>
              )}

              <div className="mt-8 flex justify-between">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="px-8 py-6 rounded-xl font-semibold border-[#C9A96E] text-[#C9A96E] hover:bg-[#C9A96E] hover:text-white"
                >
                  <ArrowLeft className="mr-2" size={20} />
                  Retour
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="bg-[#C9A96E] hover:bg-[#A67B5B] text-white px-8 py-6 rounded-xl font-semibold"
                >
                  Continuer
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Personal Information */}
          {step === 3 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-in">
              <h2 className="text-2xl font-serif font-bold text-[#2C1810] mb-6">
                Vos informations
              </h2>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Votre prénom"
                      className="h-12 border-[#D4C4B5]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Votre nom"
                      className="h-12 border-[#D4C4B5]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="h-12 border-[#D4C4B5]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="06 XX XX XX XX"
                      className="h-12 border-[#D4C4B5]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optionnel)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Informations complémentaires, demandes particulières..."
                    rows={4}
                    className="border-[#D4C4B5]"
                  />
                </div>
              </div>

              {/* Booking Summary */}
              <div className="mt-8 bg-[#F5F0EB] p-6 rounded-xl">
                <h3 className="font-semibold text-[#2C1810] mb-4">Récapitulatif</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-[#5D4037] mb-2">Services ({basket.length}) :</p>
                    {basket.map((service, idx) => (
                      <div key={idx} className="ml-4 mb-1 flex justify-between">
                        <span className="text-[#2C1810]">• {service.name}</span>
                        <span className="text-[#C9A96E] font-medium">{service.price}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-[#D4C4B5] pt-3 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[#5D4037]">Date:</span>
                      <span className="text-[#2C1810] font-medium">{formatDate(selectedDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#5D4037]">Heure:</span>
                      <span className="text-[#2C1810] font-medium">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#5D4037]">Durée totale:</span>
                      <span className="text-[#2C1810] font-medium">{basketTotals.totalDuration}</span>
                    </div>
                  </div>
                  {basketTotals.discount > 0 && (
                    <div className="border-t border-[#D4C4B5] pt-3 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-[#5D4037]">Sous-total:</span>
                        <span className="text-[#2C1810]">{basketTotals.subtotal.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-[#C9A96E] font-semibold">
                        <span>Réduction ({basketTotals.discountPercent}%):</span>
                        <span>-{basketTotals.discount.toFixed(2)}€</span>
                      </div>
                    </div>
                  )}
                  <div className="border-t border-[#D4C4B5] pt-3 flex justify-between text-lg font-bold">
                    <span className="text-[#2C1810]">Total:</span>
                    <span className="text-[#C9A96E]">{basketTotals.totalPrice.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-between">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="px-8 py-6 rounded-xl font-semibold border-[#C9A96E] text-[#C9A96E] hover:bg-[#C9A96E] hover:text-white"
                >
                  <ArrowLeft className="mr-2" size={20} />
                  Retour
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceedStep3 || submitting}
                  className="bg-[#C9A96E] hover:bg-[#A67B5B] text-white px-8 py-6 rounded-xl font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Réservation en cours...
                    </>
                  ) : (
                    <>
                      Confirmer la réservation
                      <CheckCircle className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-fade-in">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              <h2 className="text-3xl font-serif font-bold text-[#2C1810] mb-4">
                Réservation confirmée !
              </h2>
              <p className="text-[#5D4037] mb-8 max-w-md mx-auto">
                Merci {formData.firstName} ! Votre demande de rendez-vous a été enregistrée.
                Vous recevrez une confirmation par email à <strong>{formData.email}</strong>.
              </p>

              <div className="bg-[#F5F0EB] p-6 rounded-xl mb-8 max-w-md mx-auto text-left">
                <h3 className="font-semibold text-[#2C1810] mb-4">Détails du rendez-vous</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-[#5D4037]">Référence:</span> <span className="font-medium">{bookingResult?.id?.slice(0, 8) || 'N/A'}</span></p>
                  <div>
                    <p className="text-[#5D4037] mb-1">Services ({bookingResult?.services?.length || basket.length}) :</p>
                    {(bookingResult?.services || basket).map((service, idx) => (
                      <p key={idx} className="ml-4 font-medium">• {service.name}</p>
                    ))}
                  </div>
                  <p><span className="text-[#5D4037]">Date:</span> <span className="font-medium">{formatDate(selectedDate)}</span></p>
                  <p><span className="text-[#5D4037]">Heure:</span> <span className="font-medium">{bookingResult?.time || selectedTime}</span></p>
                  <p><span className="text-[#5D4037]">Durée:</span> <span className="font-medium">{bookingResult?.totalDuration || basketTotals.totalDuration}</span></p>
                  {bookingResult?.totalPrice && (
                    <p><span className="text-[#5D4037]">Total:</span> <span className="font-semibold text-[#C9A96E]">{bookingResult.totalPrice}</span></p>
                  )}
                  <p><span className="text-[#5D4037]">Lieu:</span> <span className="font-medium">{siteInfo.address}</span></p>
                </div>
              </div>

              <Button
                onClick={() => window.location.href = '/'}
                className="bg-[#C9A96E] hover:bg-[#A67B5B] text-white px-8 py-6 rounded-xl font-semibold"
              >
                Retour à l'accueil
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
